<template>
  <div v-if="available" class="pw" :class="{ 'pw--open': view.panelOpen }" :style="{ left: `${left}px` }">
    <audio ref="audioEl" autoplay playsinline class="pw-audio"></audio>

    <!-- Chamada recebida: sempre à vista, mesmo com o painel fechado -->
    <div v-for="c in incoming" :key="c.callId" class="pw-incoming" role="alertdialog" :aria-label="`Chamada de ${c.name}`">
      <div class="pw-in-pulse" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" /></svg>
      </div>
      <div class="pw-in-info">
        <span class="pw-in-label">Chamada recebida</span>
        <b class="pw-in-name">{{ c.name }}</b>
        <span v-if="c.name !== c.number" class="pw-in-num">{{ fmtNumber(c.number) }}</span>
      </div>
      <div class="pw-in-actions">
        <button type="button" class="pw-round pw-round--reject" title="Recusar" aria-label="Recusar" @click="reject(c)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85a.98.98 0 0 1-1.38-.02L.29 13.08a.96.96 0 0 1 0-1.37C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.71c.38.38.38 1 0 1.37l-2.51 2.47a.98.98 0 0 1-1.38.02 11.6 11.6 0 0 0-2.66-1.85.99.99 0 0 1-.56-.9v-3.1A15.3 15.3 0 0 0 12 9z" /></svg>
        </button>
        <button type="button" class="pw-round pw-round--answer" title="Atender" aria-label="Atender" @click="answer(c)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" /></svg>
        </button>
      </div>
      <button v-if="c.conversationId" type="button" class="pw-link pw-in-open" @click="openConversation(c)">Abrir conversa</button>
    </div>

    <!-- Painel -->
    <section v-if="view.panelOpen" class="pw-panel" aria-label="Telefone">
      <p v-if="demo" class="pw-demo">Demonstração — nada é ligado de verdade. Ctrl+- fecha, Ctrl+, simula uma chamada.</p>
      <header class="pw-head">
        <span class="pw-dot" :class="`pw-dot--${view.state}`" aria-hidden="true"></span>
        <div class="pw-head-text">
          <b>Telefone</b>
          <small>{{ stateLabel }}</small>
        </div>
        <button type="button" class="pw-switch" role="switch" :aria-checked="view.state !== 'off'" :aria-label="view.state === 'off' ? 'Ligar o telefone' : 'Desligar o telefone'" @click="togglePhone">
          <span></span>
        </button>
        <button type="button" class="pw-icon" :class="{ on: settingsOpen }" title="Dispositivos de áudio" aria-label="Dispositivos de áudio" :aria-pressed="settingsOpen" @click="toggleSettings">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
        </button>
        <button type="button" class="pw-icon" title="Minimizar" aria-label="Minimizar" @click="openPanel(false)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
      </header>

      <!-- Dispositivos de áudio: escolher, testar, e fica salvo neste navegador -->
      <div v-if="settingsOpen" class="pw-settings">
        <b class="pw-set-title">Dispositivos de áudio</b>
        <AudioDeviceSettings compact :call-active="active.length > 0" />
        <button type="button" class="pw-btn pw-btn--done" @click="toggleSettings">Pronto</button>
      </div>

      <template v-if="!settingsOpen">
      <p v-if="view.problem" class="pw-problem">{{ view.problem }}</p>
      <p v-else-if="view.state === 'off'" class="pw-hint">
        Ligue pra fazer e receber ligações aqui no navegador, pelo seu ramal. O navegador vai pedir o microfone.
      </p>

      <!-- Chamadas em andamento -->
      <div v-for="c in active" :key="c.callId" class="pw-call">
        <div class="pw-call-top">
          <div class="pw-call-info">
            <b>{{ c.name }}</b>
            <span>{{ c.name !== c.number ? fmtNumber(c.number) + ' · ' : '' }}{{ callStatusLabel(c) }}</span>
          </div>
          <span class="pw-timer">{{ c.talkingSince ? timer(c.talkingSince) : '' }}</span>
        </div>
        <div class="pw-call-actions">
          <button type="button" class="pw-act" :class="{ on: c.isMute }" :aria-pressed="c.isMute" @click="act('mute', c)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line v-if="c.isMute" x1="2" y1="2" x2="22" y2="22" /></svg>
            {{ c.isMute ? 'Sem mudo' : 'Mudo' }}
          </button>
          <button type="button" class="pw-act" :class="{ on: c.isHold }" :aria-pressed="c.isHold" :disabled="c.status !== 'talking'" @click="act('hold', c)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="9" y1="5" x2="9" y2="19" /><line x1="15" y1="5" x2="15" y2="19" /></svg>
            {{ c.isHold ? 'Retomar' : 'Espera' }}
          </button>
          <button type="button" class="pw-act" :class="{ on: keypadFor === c.callId }" :disabled="c.status !== 'talking'" @click="keypadFor = keypadFor === c.callId ? null : c.callId; transferFor = null">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="6" cy="5" r="1.6" /><circle cx="12" cy="5" r="1.6" /><circle cx="18" cy="5" r="1.6" /><circle cx="6" cy="11" r="1.6" /><circle cx="12" cy="11" r="1.6" /><circle cx="18" cy="11" r="1.6" /><circle cx="6" cy="17" r="1.6" /><circle cx="12" cy="17" r="1.6" /><circle cx="18" cy="17" r="1.6" /><circle cx="12" cy="22" r="1.6" /></svg>
            Teclado
          </button>
          <button type="button" class="pw-act" :class="{ on: transferFor === c.callId }" :disabled="c.status !== 'talking'" @click="transferFor = transferFor === c.callId ? null : c.callId; keypadFor = null">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>
            Transferir
          </button>
          <button type="button" class="pw-act pw-act--hangup" @click="act('hangup', c)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85a.98.98 0 0 1-1.38-.02L.29 13.08a.96.96 0 0 1 0-1.37C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.71c.38.38.38 1 0 1.37l-2.51 2.47a.98.98 0 0 1-1.38.02 11.6 11.6 0 0 0-2.66-1.85.99.99 0 0 1-.56-.9v-3.1A15.3 15.3 0 0 0 12 9z" /></svg>
            Desligar
          </button>
        </div>
        <div v-if="keypadFor === c.callId" class="pw-keypad" role="group" aria-label="Teclado">
          <button v-for="k in KEYS" :key="k" type="button" @click="demo ? undefined : sendDtmf(c.callId, k)">{{ k }}</button>
        </div>
        <form v-if="transferFor === c.callId" class="pw-transfer" @submit.prevent="transfer(c)">
          <input v-model="transferNumber" class="pw-input" inputmode="tel" placeholder="Ramal ou número" aria-label="Transferir para" />
          <button type="submit" class="pw-btn" :disabled="!transferNumber.trim()">Transferir</button>
        </form>
        <button v-if="c.conversationId" type="button" class="pw-link" @click="openConversation(c)">Abrir conversa</button>
      </div>

      <!-- Discador / Recentes -->
      <div v-if="view.state === 'ready' && !active.length" class="pw-tabs" role="tablist">
        <button type="button" role="tab" :aria-selected="tab === 'dial'" :class="{ on: tab === 'dial' }" @click="tab = 'dial'">Discador</button>
        <button type="button" role="tab" :aria-selected="tab === 'recent'" :class="{ on: tab === 'recent' }" @click="tab = 'recent'">
          Recentes<span v-if="missedCount" class="pw-badge">{{ missedCount }}</span>
        </button>
      </div>
      <ul v-if="view.state === 'ready' && !active.length && tab === 'recent'" class="pw-recent">
        <li v-if="!recents.length" class="pw-recent-empty">Nenhuma ligação ainda.</li>
        <li v-for="r in recents" :key="r.id" class="pw-recent-row">
          <span class="pw-recent-ico" :class="recentClass(r)" :title="recentLabel(r)" aria-hidden="true">{{ r.direction === 'Inbound' ? '↙' : '↗' }}</span>
          <div class="pw-recent-info">
            <b>{{ r.contactName || fmtNumber(r.number) }}</b>
            <span>{{ recentLabel(r) }} · {{ when(r.startedAt) }}</span>
          </div>
          <button type="button" class="pw-recent-call" :title="`Ligar pra ${r.contactName || r.number}`" :aria-label="`Ligar pra ${r.contactName || r.number}`" @click="redial(r)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" /></svg>
          </button>
          <button v-if="r.conversationId" type="button" class="pw-recent-open" title="Abrir conversa" aria-label="Abrir conversa" @click="requestOpenChat(r.conversationId!)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          </button>
        </li>
      </ul>
      <form v-if="view.state === 'ready' && !active.length && tab === 'dial'" class="pw-dial" @submit.prevent="dial">
        <input v-model="dialNumber" class="pw-input pw-input--big" inputmode="tel" placeholder="Número ou ramal" aria-label="Número pra ligar" />
        <div class="pw-keypad">
          <button v-for="k in KEYS" :key="k" type="button" @click="dialNumber += k">{{ k }}</button>
        </div>
        <button type="submit" class="pw-call-btn" :disabled="!dialNumber.trim() || dialing">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" /></svg>
          {{ dialing ? 'Chamando…' : 'Ligar' }}
        </button>
      </form>
      </template>
    </section>

    <!-- Botão flutuante -->
    <button v-if="!view.panelOpen" type="button" class="pw-fab" :class="`pw-fab--${view.state}`" :aria-label="`Telefone: ${stateLabel}`" @click="openPanel(true)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" /></svg>
      <span class="pw-fab-label">{{ active.length ? `${active[0].name} · ${active[0].talkingSince ? timer(active[0].talkingSince) : callStatusLabel(active[0])}` : view.state === 'ready' ? `Ramal ${view.extension}` : 'Telefone' }}</span>
      <span class="pw-dot" :class="`pw-dot--${view.state}`" aria-hidden="true"></span>
    </button>
  </div>
</template>

<script setup lang="ts">
// Telefone no navegador (Yeastar WebRTC SDK): botão flutuante no canto, que abre o painel com o discador, as
// chamadas em andamento (mudo, espera, teclado, transferir, desligar) e a chamada recebida (atender/recusar, com o
// contato reconhecido e "Abrir conversa"). Só aparece pra quem pode ligar pelo Linkus (configurado + ramal).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { loadLinkus, useLinkus } from '@/composables/useLinkus'
import { requestOpenChat } from '@/composables/useChatTrigger'
import { listMyCalls, type CallSummary } from '@/services/wootdashLinkus'
import { applySink, effectiveDevices, refreshDevices } from '@/composables/useAudioDevices'
import AudioDeviceSettings from '@/components/AudioDeviceSettings.vue'
import {
  answerCall, connectPhone, disconnectPhone, hangupCall, phonePreference, placeCall, rejectCall, sendDtmf, setPanelOpen,
  toggleHold, toggleMute, transferCall, useBrowserPhone, type PhoneCall,
} from '@/composables/useBrowserPhone'

const props = defineProps<{ sidebarOpen: boolean }>()

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const userKey = computed<string | null>(() => store.getters.authUser?.email ?? null)
const { availability } = useLinkus()
const { phone } = useBrowserPhone()

// Demonstração (atalhos secretos): Ctrl+- mostra o painel/discador, Ctrl+, simula uma chamada recebida. Só a tela —
// nada vai pro PABX. Serve pra ver o visual antes de o Linkus estar configurado.
type DemoCall = PhoneCall
const demo = ref<{ calls: DemoCall[]; panelOpen: boolean } | null>(null)
const view = computed(() => demo.value
  ? { state: 'ready' as const, problem: null, extension: '1001', calls: demo.value.calls, panelOpen: demo.value.panelOpen, remoteStream: null }
  : phone)

function demoCall(direction: 'inbound' | 'outbound', number: string, name: string): DemoCall {
  return { callId: `demo-${Date.now()}`, number, name, direction, status: direction === 'inbound' ? 'ringing' : 'calling',
    isHold: false, isMute: false, talkingSince: null, contactId: null, conversationId: null, startedAt: Date.now(), rejected: false }
}
function onSecretKeys(e: KeyboardEvent) {
  if (!e.ctrlKey || e.altKey || e.metaKey) return
  if (e.key === '-' || e.code === 'Minus' || e.code === 'NumpadSubtract') {
    e.preventDefault()
    demo.value = demo.value ? null : { calls: [], panelOpen: true }
  } else if (e.key === ',' || e.code === 'Comma') {
    e.preventDefault()
    if (!demo.value) demo.value = { calls: [], panelOpen: false }
    demo.value.calls = [...demo.value.calls.filter(c => c.status !== 'ringing'), demoCall('inbound', '021959431661', 'Henrique (exemplo)')]
  }
}
function openPanel(open: boolean) {
  if (demo.value) demo.value.panelOpen = open
  else setPanelOpen(open)
}
/** Ação de um botão da chamada: no modo demonstração só mexe na tela. */
function act(kind: 'mute' | 'hold' | 'hangup', c: PhoneCall) {
  if (!demo.value) {
    if (kind === 'mute') toggleMute(c)
    else if (kind === 'hold') toggleHold(c)
    else if (kind === 'hangup') hangupCall(c.callId)
    return
  }
  const call = demo.value.calls.find(x => x.callId === c.callId)
  if (!call) return
  if (kind === 'mute') call.isMute = !call.isMute
  else if (kind === 'hold') call.isHold = !call.isHold
  else if (kind === 'hangup') demo.value.calls = demo.value.calls.filter(x => x.callId !== c.callId)
}

const available = computed(() => !!availability.value?.available || !!demo.value)
const left = computed(() => (props.sidebarOpen ? 220 : 64) + 16)
const incoming = computed(() => view.value.calls.filter(c => c.direction === 'inbound' && c.status === 'ringing'))
const active = computed(() => view.value.calls.filter(c => !(c.direction === 'inbound' && c.status === 'ringing')))

const audioEl = ref<HTMLAudioElement | null>(null)
const dialNumber = ref('')
const dialing = ref(false)
const keypadFor = ref<string | null>(null)
const transferFor = ref<string | null>(null)
const transferNumber = ref('')
const now = ref(Date.now())
const tab = ref<'dial' | 'recent'>('dial')
const realRecents = ref<CallSummary[]>([])
const DEMO_RECENTS: CallSummary[] = [
  { id: 'd1', number: '021959431661', direction: 'Inbound', source: 'Browser', outcome: 'Answered', startedAt: new Date(Date.now() - 12 * 60000).toISOString(), talkSeconds: 252, contactId: null, contactName: 'Henrique (exemplo)', conversationId: null },
  { id: 'd2', number: '011987654321', direction: 'Outbound', source: 'Browser', outcome: 'NoAnswer', startedAt: new Date(Date.now() - 50 * 60000).toISOString(), talkSeconds: null, contactId: null, contactName: 'Maria (exemplo)', conversationId: null },
  { id: 'd3', number: '021933334444', direction: 'Inbound', source: 'Browser', outcome: 'Missed', startedAt: new Date(Date.now() - 3 * 3600000).toISOString(), talkSeconds: null, contactId: null, contactName: null, conversationId: null },
]
const recents = computed(() => demo.value ? DEMO_RECENTS : realRecents.value)
const missedCount = computed(() => recents.value.filter(r => r.outcome === 'Missed' && Date.now() - new Date(r.startedAt).getTime() < 86400000).length)
async function loadRecents() {
  if (token.value && available.value && !demo.value) realRecents.value = await listMyCalls(token.value)
}
function recentLabel(r: CallSummary) {
  const talk = r.talkSeconds ? ` · ${Math.floor(r.talkSeconds / 60)}min ${String(r.talkSeconds % 60).padStart(2, '0')}s` : ''
  return {
    Answered: (r.direction === 'Inbound' ? 'Recebida' : 'Feita') + talk,
    NoAnswer: 'Não atendeu',
    Missed: 'Perdida',
    Rejected: 'Recusada',
    Failed: 'Não completou',
    Requested: 'Pelo Linkus',
  }[r.outcome]
}
function recentClass(r: CallSummary) {
  return r.outcome === 'Answered' ? 'ok' : r.outcome === 'Missed' || r.outcome === 'Rejected' ? 'bad' : 'muted'
}
function when(iso: string) {
  const d = new Date(iso)
  const today = new Date().toDateString() === d.toDateString()
  return today ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
function redial(r: CallSummary) {
  tab.value = 'dial'
  dialNumber.value = r.number
  void dial()
}
let tick: ReturnType<typeof setInterval> | null = null

const stateLabel = computed(() => ({
  off: 'Desligado',
  connecting: 'Conectando ao PABX…',
  ready: `Pronto · ramal ${view.value.extension}`,
  error: 'Com problema',
}[view.value.state]))

function callStatusLabel(c: PhoneCall) {
  if (c.isHold) return 'em espera'
  return { ringing: 'tocando', calling: 'chamando…', connecting: 'conectando…', talking: 'em ligação' }[c.status]
}
function timer(since: number) {
  const s = Math.max(0, Math.floor((now.value - since) / 1000))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}
function fmtNumber(n: string) {
  const d = n.replace(/\D/g, '').replace(/^0+/, '').replace(/^55(?=\d{10,11}$)/, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return n
}

async function togglePhone() {
  if (demo.value) { demo.value = null; return }
  if (phone.state === 'off' || phone.state === 'error') await connectPhone(token.value, userKey.value)
  else disconnectPhone(userKey.value)
}

async function dial() {
  if (demo.value) {
    const call = demoCall('outbound', dialNumber.value.trim(), dialNumber.value.trim())
    demo.value.calls = [...demo.value.calls, call]
    dialNumber.value = ''
    setTimeout(() => { const c = demo.value?.calls.find(x => x.callId === call.callId); if (c) { c.status = 'talking'; c.talkingSince = Date.now() } }, 2500)
    return
  }
  dialing.value = true
  try {
    await placeCall(dialNumber.value.trim())
    dialNumber.value = ''
  } catch (e) {
    Swal.fire({ icon: 'warning', title: 'Não deu pra ligar', text: (e as Error).message })
  } finally {
    dialing.value = false
  }
}
async function answer(c: PhoneCall) {
  if (demo.value) {
    const call = demo.value.calls.find(x => x.callId === c.callId)
    if (call) { call.status = 'talking'; call.talkingSince = Date.now() }
    demo.value.panelOpen = true
    return
  }
  try {
    await answerCall(c.callId)
    setPanelOpen(true)
  } catch (e) {
    Swal.fire({ icon: 'warning', title: 'Não deu pra atender', text: (e as Error).message })
  }
}
function reject(c: PhoneCall) {
  if (demo.value) { demo.value.calls = demo.value.calls.filter(x => x.callId !== c.callId); return }
  rejectCall(c.callId)
}
function transfer(c: PhoneCall) {
  if (demo.value) { demo.value.calls = demo.value.calls.filter(x => x.callId !== c.callId); transferFor.value = null; transferNumber.value = ''; return }
  transferCall(c.callId, transferNumber.value.trim())
  transferNumber.value = ''
  transferFor.value = null
}
function openConversation(c: PhoneCall) {
  if (c.conversationId) requestOpenChat(c.conversationId)
}

// Áudio da chamada no <audio> (o SDK entrega o MediaStream; quem toca é o navegador).
watch(() => phone.remoteStream, stream => {
  if (audioEl.value) audioEl.value.srcObject = (stream as MediaStream | null) ?? null
})

// ── Dispositivos de áudio (escolha e teste em AudioDeviceSettings.vue / useAudioDevices.ts) ──────────
const settingsOpen = ref(false)
function toggleSettings() { settingsOpen.value = !settingsOpen.value }

// A conversa sai pela saída escolhida (e volta pro padrão se o dispositivo sumir).
watch([audioEl, () => effectiveDevices.value.speakerId], ([el, id]) => { void applySink(el, id) }, { immediate: true })

// Liga sozinho se o agente deixou ligado da última vez.
watch([available, token], ([ok, t]) => {
  if (ok && t && phone.state === 'off' && phonePreference(userKey.value)) connectPhone(t, userKey.value)
}, { immediate: true })

watch(() => [phone.historyVersion, phone.state, phone.panelOpen], () => { if (phone.panelOpen && phone.state === 'ready') loadRecents() })

onMounted(() => {
  void loadLinkus(token.value)
  tick = setInterval(() => { now.value = Date.now() }, 1000)
  window.addEventListener('keydown', onSecretKeys)
  // Lista os dispositivos (sem pedir permissão) pra saber se o escolhido ainda está conectado.
  void refreshDevices()
})
onBeforeUnmount(() => {
  if (tick) clearInterval(tick)
  window.removeEventListener('keydown', onSecretKeys)
})
</script>

<style scoped>
.pw { position: fixed; bottom: 16px; z-index: 900; display: flex; flex-direction: column; align-items: flex-start; gap: 10px; transition: left .2s ease; font-family: inherit; }
.pw-audio { display: none; }
.pw-demo { margin: 0; padding: 7px 14px; font-size: 11px; color: #92400e; background: #fffbeb; border-bottom: 1px solid #fde68a; }

.pw-fab {
  display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 14px 0 12px; border-radius: 999px; border: none; cursor: pointer;
  background: #1f2633; color: #fff; font: inherit; font-size: 12.5px; font-weight: 600; box-shadow: 0 6px 20px rgba(15, 23, 42, .25);
  max-width: 280px; transition: transform .14s ease;
}
.pw-fab:hover { transform: translateY(-1px); }
.pw-fab:focus-visible { outline: 2px solid var(--red); outline-offset: 2px; }
.pw-fab--ready { background: linear-gradient(135deg, #22c55e, #16a34a); }
.pw-fab-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
.pw-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: #9ca3af; }
.pw-dot--ready { background: #bbf7d0; box-shadow: 0 0 0 3px rgba(187, 247, 208, .3); }
.pw-dot--connecting { background: #fde68a; animation: pw-blink 1s ease-in-out infinite; }
.pw-dot--error { background: #fca5a5; }
.pw-panel .pw-dot--ready { background: #16a34a; box-shadow: 0 0 0 3px rgba(22, 163, 74, .18); }
@keyframes pw-blink { 50% { opacity: .35; } }

.pw-panel { width: 300px; max-width: calc(100vw - 32px); background: var(--white); border: 1px solid var(--ink-10); border-radius: 14px; box-shadow: 0 12px 36px rgba(15, 23, 42, .22); overflow: hidden; }
.pw-head { display: flex; align-items: center; gap: 10px; padding: 12px 12px 12px 14px; border-bottom: 1px solid var(--ink-10); }
.pw-head-text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pw-head-text b { font-size: 13.5px; color: var(--ink-80); }
.pw-head-text small { font-size: 11.5px; color: var(--ink-40); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pw-switch { width: 34px; height: 19px; border-radius: 999px; border: none; padding: 0; background: var(--ink-10); position: relative; cursor: pointer; flex-shrink: 0; }
.pw-switch span { position: absolute; top: 2px; left: 2px; width: 15px; height: 15px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.2); transition: left .15s; }
.pw-switch[aria-checked="true"] { background: #16a34a; }
.pw-switch[aria-checked="true"] span { left: 17px; }
.pw-switch:focus-visible, .pw-icon:focus-visible { outline: 2px solid var(--red); outline-offset: 2px; }
.pw-icon { background: none; border: none; color: var(--ink-40); cursor: pointer; padding: 4px; border-radius: 6px; display: flex; }
.pw-icon:hover { background: var(--ink-05); color: var(--ink-80); }
.pw-problem { margin: 10px 14px 0; font-size: 12px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 8px 10px; line-height: 1.45; }
.pw-hint { margin: 10px 14px 12px; font-size: 12px; color: var(--ink-40); line-height: 1.5; }

.pw-call { padding: 12px 14px; border-bottom: 1px solid var(--ink-05); display: flex; flex-direction: column; gap: 10px; }
.pw-call-top { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; }
.pw-call-info { display: flex; flex-direction: column; min-width: 0; }
.pw-call-info b { font-size: 14px; color: var(--ink-80); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pw-call-info span { font-size: 12px; color: var(--ink-40); }
.pw-timer { font-size: 13px; font-weight: 700; color: #16a34a; font-variant-numeric: tabular-nums; }
.pw-call-actions { display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px; }
.pw-act { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 7px 2px; border: none; border-radius: 9px; background: var(--ink-05); color: var(--ink-70); font: inherit; font-size: 10.5px; font-weight: 600; cursor: pointer; }
.pw-act:hover:not(:disabled) { background: var(--ink-10); }
.pw-act.on { background: #1f2633; color: #fff; }
.pw-act:disabled { opacity: .4; cursor: not-allowed; }
.pw-act--hangup { background: #dc2626; color: #fff; }
.pw-act--hangup:hover { background: #b91c1c !important; }
.pw-act:focus-visible { outline: 2px solid var(--red); outline-offset: 1px; }

.pw-keypad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.pw-keypad button { height: 38px; border-radius: 10px; border: 1px solid var(--ink-10); background: var(--white); font: inherit; font-size: 16px; font-weight: 600; color: var(--ink-80); cursor: pointer; font-variant-numeric: tabular-nums; }
.pw-keypad button:hover { background: var(--ink-05); }
.pw-keypad button:active { background: var(--ink-10); }
.pw-transfer { display: flex; gap: 6px; }
.pw-input { flex: 1; min-width: 0; padding: 8px 10px; border: 1px solid var(--ink-10); border-radius: 9px; font: inherit; font-size: 13px; color: var(--ink-80); background: var(--white); }
.pw-input:focus { border-color: var(--red); outline: none; }
.pw-input--big { font-size: 18px; text-align: center; letter-spacing: .04em; font-variant-numeric: tabular-nums; }
.pw-btn { padding: 0 12px; border-radius: 9px; border: none; background: #1f2633; color: #fff; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.pw-btn:disabled { opacity: .45; cursor: not-allowed; }
.pw-tabs { display: flex; gap: 2px; margin: 10px 14px 0; background: var(--ink-05); padding: 3px; border-radius: 9px; }
.pw-tabs button { flex: 1; border: none; background: transparent; padding: 6px; border-radius: 7px; font: inherit; font-size: 12px; font-weight: 600; color: var(--ink-60); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
.pw-tabs button.on { background: var(--white); color: var(--ink-80); box-shadow: 0 1px 3px rgba(15,23,42,.12); }
.pw-badge { min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px; background: #dc2626; color: #fff; font-size: 10px; display: inline-flex; align-items: center; justify-content: center; }
.pw-recent { list-style: none; margin: 8px 0 0; padding: 0 6px 8px; max-height: 300px; overflow-y: auto; }
.pw-recent-empty { padding: 18px 8px; text-align: center; font-size: 12px; color: var(--ink-40); }
.pw-recent-row { display: flex; align-items: center; gap: 10px; padding: 8px; border-radius: 9px; }
.pw-recent-row:hover { background: var(--ink-02, #f7f8fa); }
.pw-recent-ico { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.pw-recent-ico.ok { background: rgba(22,163,74,.12); color: #16a34a; }
.pw-recent-ico.bad { background: rgba(220,38,38,.1); color: #dc2626; }
.pw-recent-ico.muted { background: var(--ink-05); color: var(--ink-40); }
.pw-recent-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pw-recent-info b { font-size: 12.5px; color: var(--ink-80); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pw-recent-info span { font-size: 11px; color: var(--ink-40); font-variant-numeric: tabular-nums; }
.pw-recent-call, .pw-recent-open { width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pw-recent-call { background: rgba(22,163,74,.12); color: #16a34a; }
.pw-recent-call:hover { background: #16a34a; color: #fff; }
.pw-recent-open { background: var(--ink-05); color: var(--ink-60); }
.pw-recent-open:hover { background: var(--ink-10); }
.pw-dial { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 10px; }
.pw-call-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 40px; border-radius: 999px; border: none; background: linear-gradient(135deg, #22c55e, #16a34a); color: #fff; font: inherit; font-size: 13.5px; font-weight: 700; cursor: pointer; box-shadow: 0 2px 10px rgba(22, 163, 74, .35); }
.pw-call-btn:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }
.pw-link { align-self: flex-start; background: none; border: none; padding: 0; font: inherit; font-size: 12px; font-weight: 600; color: #2563eb; cursor: pointer; }
.pw-link:hover { text-decoration: underline; }

.pw-incoming { width: 300px; max-width: calc(100vw - 32px); display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 14px; background: #1f2633; color: #fff; box-shadow: 0 12px 36px rgba(15, 23, 42, .35); animation: pw-in .2s ease-out; }
@keyframes pw-in { from { transform: translateY(8px); opacity: 0; } }
.pw-in-pulse { width: 40px; height: 40px; border-radius: 50%; background: #16a34a; display: flex; align-items: center; justify-content: center; animation: pw-ring 1.2s ease-in-out infinite; }
@keyframes pw-ring { 0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, .55); } 60% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); } }
.pw-in-info { display: flex; flex-direction: column; min-width: 0; }
.pw-in-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; color: #bbf7d0; font-weight: 700; }
.pw-in-name { font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pw-in-num { font-size: 12px; color: #cbd5e1; font-variant-numeric: tabular-nums; }
.pw-in-actions { display: flex; gap: 8px; }
.pw-round { width: 38px; height: 38px; border-radius: 50%; border: none; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.pw-round--answer { background: #16a34a; }
.pw-round--reject { background: #dc2626; }
.pw-round:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.pw-in-open { grid-column: 1 / -1; color: #93c5fd; }
@media (prefers-reduced-motion: reduce) { .pw-in-pulse, .pw-dot--connecting, .pw-incoming { animation: none; } }

/* Dispositivos de áudio — só variáveis de tema (vale no claro e no escuro). */
.pw-icon.on { background: var(--ink-05); color: var(--red); }
.pw-settings { display: flex; flex-direction: column; gap: 12px; padding: 12px 14px 14px; }
.pw-set-title { font-size: 12.5px; color: var(--ink-80); }
.pw-btn--done { height: 34px; align-self: flex-end; background: var(--red); }
</style>
